import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { useSnackbar } from 'notistack';
import { observer } from 'mobx-react';
import {
  Box,
  Paper,
  Button,
  Divider,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Typography,
  Autocomplete,
  Switch,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { getMessage } from 'helpers/helper';

import { routes } from 'config/index';
import WinesService from 'services/winesService';
import PartnersService from 'services/partnersService';
import domainData from 'store/domainStore';
import wineStore from 'store/wineStore';

import ImageInput from 'components/common/form/imageInput';
import ImageCropWrapper from 'components/common/form/ImageCropWrapper';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import ButtonsGroup from 'components/common/customBtnGroup';
import TemplateImageModal from 'components/common/templateImageModal';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ImageIcon from '@mui/icons-material/Image';
import useIsTallEnough from 'hooks/useIsTallEnough';
import useImageTemplates from 'hooks/useImageTemplates';

import M from 'messages';
import formClasses from 'components/common/form/styles';
import classes from './styles';
import PriceUnits from './priceUnits';
import Ratings from './ratings';
import WineSpecs from './wineSpecs';
import configOptions from '../config/config';

import SPECIALS from '../../../constants/specials';
import PAIRING from '../../../constants/pairing';

const maxSymbols = 255;
const WINE_IMAGE_SECTION = 'Wine Image';

const CreateMenuItemWrapper = observer(({ isEdit, isCreate }) => {
  const isUserSuperAdmin = domainData.userIsSuperAdmin();
  const currentSPId = domainData.getCurrentSP();
  const menuItemMode = isEdit ? 'edit' : isCreate ? 'create' : '';
  wineStore.setWineState(menuItemMode);
  return (
    <CreateMenuItem isEdit={isEdit} isCreate={isCreate} currentSPId={currentSPId} isUserSuperAdmin={isUserSuperAdmin} />
  );
});

const CreateMenuItem = observer(({ isEdit = false, isCreate = false, currentSPId, isUserSuperAdmin }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [spLisLoading, setSpLisLoading] = useState(false);
  const [currMenuItemData, setCurrMenuItemData] = useState(null);
  const [disableSave, setDisableSave] = useState(false);
  const [useCrop, setUseCrop] = useState(false);
  const allowedWindowHeight = useIsTallEnough(645);
  const data = wineStore.getStoreWineForCreate();
  const dataErrors = wineStore.getWineDataErrors();
  const spList = wineStore.getSpList;
  let readonly = false;
  let disabled = false;

  const { open, openModal, closeModal, currentTemplates, currentLoading } = useImageTemplates();

  if (!isUserSuperAdmin && !isCreate && !data.active) {
    disabled = true;
    readonly = true;
  }
  const getUrlFieldHelperText = name => {
    let helperText = '';
    if (dataErrors[name] && dataErrors[name] === 'required') {
      helperText = M.get('form.errors.required');
    } else if (dataErrors[name] && dataErrors[name] === 'invalidUrl') {
      helperText = M.get(`form.errors.invalidUrl`);
    }
    return helperText;
  };

  const setReadyData = readyData => {
    wineStore.setWineData(readyData, readyData.serviceProvider);
    setCurrMenuItemData(readyData);
    setLoading(false);
  };

  const getWine = useCallback(
    async () => {
      try {
        setLoading(true);
        const response = await WinesService.getWine(id);
        if (response) {
          setReadyData(response.data);
        }
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, id],
  );

  const actionSucceeded = response => {
    enqueueSnackbar(getMessage(response.data), { variant: 'success' });
    setDisableSave(false);
  };

  const createWine = useCallback(
    async newData => {
      try {
        setDisableSave(true);
        const response = await WinesService.createWine(newData);
        if (response) {
          actionSucceeded(response);
          const baseRoute = location.pathname.replace('/create', '');
          navigate(baseRoute);
        }
      } catch (err) {
        setDisableSave(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  const updateWine = useCallback(
    async newData => {
      try {
        setDisableSave(true);
        const response = await WinesService.updateWine(newData, id);
        if (response) {
          actionSucceeded(response);
          navigate(routes.wines.path);
        }
      } catch (err) {
        setDisableSave(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  const getSpList = useCallback(
    async () => {
      try {
        setSpLisLoading(true);
        if (isUserSuperAdmin) {
          const response = await domainData.getSPListNames();
          if (response) {
            wineStore.setSpList(response);
          }
        } else {
          const response = await PartnersService.getPartner(currentSPId);
          if (response) {
            wineStore.setSpList([response.data]);
            wineStore.setProducerDefaultValue(response.data.name);
          }
        }
        setSpLisLoading(false);
      } catch (err) {
        setSpLisLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    if (isCreate) {
      wineStore.setWineState('create');
      wineStore.setServiceProviderId(currentSPId);
      setLoading(false);
      getSpList();
    } else if (id) {
      wineStore.clearWineDataErrors();
      getWine(id);
    }
    wineStore.loadDefaultWineImage();
    wineStore.getCountriesData();
    wineStore.getRatingsData();
    wineStore.getWineTypesData();
    return () => {
      wineStore.clearTypeAutocompleteData();
      wineStore.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSPDisabled = () => {
    if (!isCreate) {
      return true;
    }
    return !!currentSPId;
  };

  const handleDiscard = () => {
    navigate(routes.wines.path);
  };

  const onSubmit = async event => {
    event.preventDefault();
    const formHasError = wineStore.wineDataHasError();
    if (!formHasError) {
      try {
        const formData = wineStore.getWineData(isEdit);
        if (isEdit) {
          updateWine(formData);
        } else {
          createWine(formData);
        }
      } catch (err) {
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
      return true;
    }
    enqueueSnackbar(M.get('form.errors.notValidForm'), { variant: 'error' });
    return false;
  };

  const isFormChanged = () => {
    const cloneMenuItemData = JSON.parse(JSON.stringify(currMenuItemData));
    const dataStored = JSON.parse(JSON.stringify(data));
    const condition =
      cloneMenuItemData && dataStored ? JSON.stringify(cloneMenuItemData) !== JSON.stringify(dataStored) : false;
    return condition || wineStore.getCreateFormChanged();
  };

  const isSaveButtonDisabled = () =>
    isCreate
      ? disabled || !wineStore.getCreateFormChanged() || disableSave
      : disabled || !isFormChanged() || disableSave;

  const getOptionLabel = option => {
    // Value selected with enter, right from the input
    if (typeof option === 'string') {
      return option;
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return option.name;
  };
  const isOptionEqualToValue = (option, value) => option.id === value.id;

  const years = Array.from({ length: 61 }, (_, index) => (2030 - index).toString());
  const yearOptions = ['NV', ...years];

  if (loading || (data.serviceProviderId && !wineStore.getAllOptionsLoaded())) return <Loading />;

  return (
    <Box sx={classes.page}>
      <PageHeader
        generalPageTitle={`${M.get('wines.pageTitle')} | ${isCreate ? M.get('actions.create') : currMenuItemData?.name || ''}`}
        onlyGeneralPageTitle
      />
      {/* Content */}
      <Box sx={classes.pageContent}>
        <form onSubmit={onSubmit} noValidate>
          <Box component={Paper} sx={classes.boxContainer} pb={2} px={3} pt={3}>
            <Typography sx={classes.secondaryTitle}>{M.get('menuItem.productDetails')}</Typography>
            <Grid container spacing={3} mb={3} mt={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    label={`${M.get('menuItem.name')}`}
                    id="name"
                    name="name"
                    value={data.name}
                    disabled={disabled}
                    required
                    onChange={wineStore.onChangeField}
                    variant="outlined"
                    error={!!dataErrors.name}
                    inputProps={{ maxLength: maxSymbols }}
                    helperText={
                      dataErrors.name ? (
                        M.get('form.errors.required')
                      ) : (
                        <Box component="span" sx={classes.helperDiv}>
                          <Box component="span">
                            {data.name?.length || 0}/{maxSymbols}
                          </Box>
                        </Box>
                      )
                    }
                  />
                </FormControl>
              </Grid>
              {isUserSuperAdmin && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel
                      id="sp-select-label"
                      sx={{
                        ...classes.placeholderColor,
                        ...(dataErrors.serviceProviderId && formClasses.invalidSelect),
                      }}>
                      {`${M.get('menuItem.serviceProvider')} *`}
                    </InputLabel>
                    <Select
                      id="sp-select"
                      variant="outlined"
                      name="serviceProviderId"
                      labelId="sp-select-label"
                      label={M.get('menuItem.serviceProvider')}
                      required
                      disabled={disabled || isSPDisabled()}
                      value={data.serviceProviderId ?? ''}
                      defaultValue={data.serviceProviderId}
                      error={!!dataErrors.serviceProviderId}
                      onChange={wineStore.onChangeField}
                      MenuProps={{
                        classes: { paper: classes.menuPaper },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'center',
                        },
                      }}>
                      {spLisLoading ? (
                        <MenuItem value={data.serviceProviderId} disabled>
                          {M.get('menuItem.loading')}
                        </MenuItem>
                      ) : isCreate ? (
                        spList?.map(opt => (
                          <MenuItem key={opt.id || opt.value} value={opt.id || opt.value}>
                            {opt.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={data.serviceProviderId}>{data.serviceProvider?.name}</MenuItem>
                      )}
                    </Select>
                    {dataErrors.serviceProviderId && (
                      <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    label={`${M.get('menuItem.producer')}`}
                    id="producer"
                    name="producer"
                    value={data.producer}
                    disabled={disabled}
                    required
                    onChange={wineStore.onChangeField}
                    variant="outlined"
                    error={!!dataErrors.producer}
                    inputProps={{ maxLength: maxSymbols }}
                    helperText={
                      dataErrors.producer ? (
                        M.get('form.errors.required')
                      ) : (
                        <Box component="span" sx={classes.helperDiv}>
                          <Box component="span">
                            {data.producer?.length || 0}/{maxSymbols}
                          </Box>
                        </Box>
                      )
                    }
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    id="typeId"
                    variant="outlined"
                    clearOnBlur
                    selectOnFocus
                    forcePopupIcon
                    handleHomeEndKeys
                    disabled={disabled}
                    value={wineStore.getSelectedWineType}
                    defaultValue={wineStore.getSelectedWineType}
                    onChange={(e, value) => wineStore.onChangeAutocomplete(value, 'typeId', 'wineTypes')}
                    isOptionEqualToValue={isOptionEqualToValue}
                    options={wineStore.wineTypesOptions}
                    getOptionLabel={getOptionLabel}
                    getOptionDisabled={option => option.isDisabled}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={props => (
                      <TextField
                        {...props}
                        required
                        label={`${M.get('menuItem.wineType')}`}
                        variant="outlined"
                        error={!!dataErrors.typeId}
                        helperText={!!dataErrors.typeId && M.get('form.errors.required')}
                        inputProps={{
                          ...props.inputProps,
                          maxLength: maxSymbols,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <Autocomplete
                    id="countryId"
                    variant="outlined"
                    clearOnBlur
                    selectOnFocus
                    forcePopupIcon
                    handleHomeEndKeys
                    disabled={disabled}
                    value={wineStore.getSelectedOption}
                    defaultValue={wineStore.getSelectedOption}
                    onChange={(e, value) => wineStore.onChangeAutocomplete(value, 'countryId', 'countries')}
                    options={wineStore.countriesOptions}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={isOptionEqualToValue}
                    getOptionDisabled={option => option.isDisabled}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          {option.name}
                        </li>
                      );
                    }}
                    renderInput={props => (
                      <TextField
                        {...props}
                        required
                        label={`${M.get('menuItem.countries')}`}
                        variant="outlined"
                        error={dataErrors.countryId}
                        helperText={!!dataErrors.countryId && M.get('form.errors.required')}
                        inputProps={{
                          ...props.inputProps,
                          maxLength: maxSymbols,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="year-select-label">{M.get('menuItem.wineYear')}</InputLabel>
                  <Select
                    labelId="year"
                    id="year-select"
                    name="year"
                    label={`${M.get('menuItem.wineYear')} *`}
                    value={data.year || ''}
                    onChange={wineStore.onChangeField}
                    error={!!dataErrors.year}
                    MenuProps={{
                      classes: { paper: classes.menuPaper },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                    }}>
                    <MenuItem key="None" value="">
                      None
                    </MenuItem>
                    {yearOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {dataErrors.year && (
                    <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    required
                    id="region"
                    name="region"
                    variant="outlined"
                    disabled={disabled}
                    value={data.region}
                    onChange={wineStore.onChangeField}
                    label={`${M.get('menuItem.region')}`}
                    error={!!dataErrors.region}
                    inputProps={{ maxLength: maxSymbols }}
                    helperText={
                      dataErrors.region ? (
                        M.get('form.errors.required')
                      ) : (
                        <Box component="span" sx={classes.helperDiv}>
                          <Box component="span">
                            {data.region?.length || 0}/{maxSymbols}
                          </Box>
                        </Box>
                      )
                    }
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    id="whereToBuy"
                    name="whereToBuy"
                    variant="outlined"
                    disabled={disabled}
                    value={data.whereToBuy || ''}
                    onChange={e => wineStore.onChangeUrlField(e)}
                    label={M.get('menuItem.whereToBuy')}
                    InputProps={{
                      inputProps: { maxLength: maxSymbols },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip placement="top" title={M.get('menuItem.whereToBuyTooltip')}>
                            <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    error={!!dataErrors.whereToBuy}
                    helperText={
                      dataErrors.whereToBuy ? (
                        getUrlFieldHelperText('whereToBuy')
                      ) : (
                        <Box component="span" sx={classes.helperDiv}>
                          <Box component="span">
                            {data.whereToBuy?.length || 0}/{maxSymbols}
                          </Box>
                        </Box>
                      )
                    }
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    id="subRegion"
                    name="subRegion"
                    variant="outlined"
                    disabled={disabled}
                    value={data.subRegion}
                    onChange={wineStore.onChangeField}
                    label={M.get('menuItem.subRegion')}
                    inputProps={{ maxLength: maxSymbols }}
                    helperText={
                      <Box component="span" sx={classes.helperDiv}>
                        <Box component="span">
                          {data.subRegion?.length || 0}/{maxSymbols}
                        </Box>
                      </Box>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Divider />
                <Box sx={classes.sectionHeaderText}>
                  <Typography sx={classes.fieldHeaderText} component="h1">
                    {M.get(configOptions.colorGroup.label)}
                  </Typography>
                </Box>
                <Grid mt={1} mb={2}>
                  <ButtonsGroup
                    disabled={disabled}
                    groupObject={configOptions.colorGroup}
                    handleChange={wineStore.onChangeButtonGroup}
                    value={data.color}
                  />
                </Grid>
                <Divider sx={dataErrors.color && classes.errorHelperText} />
                {dataErrors.color && (
                  <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Box sx={classes.sectionHeaderText}>
                  <Typography sx={classes.fieldHeaderText} component="h1">
                    {M.get(configOptions.sweetnessGroup.label)}
                  </Typography>
                </Box>
                <Grid mt={1} mb={2}>
                  <ButtonsGroup
                    disabled={disabled}
                    groupObject={configOptions.sweetnessGroup}
                    handleChange={wineStore.onChangeButtonGroup}
                    value={data.sweetness}
                  />
                </Grid>
                <Divider sx={dataErrors.sweetness && classes.errorHelperText} />
                {dataErrors.sweetness && (
                  <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Box sx={classes.sectionHeaderText}>
                  <Typography sx={classes.fieldHeaderText} component="h1">
                    {M.get(configOptions.styleGroup.label)}
                  </Typography>
                </Box>
                <Grid mt={1} mb={2}>
                  <ButtonsGroup
                    disabled={disabled}
                    groupObject={configOptions.styleGroup}
                    handleChange={wineStore.onChangeButtonGroup}
                    value={data.style}
                  />
                </Grid>
                <Divider sx={dataErrors.style && classes.errorHelperText} />
                {dataErrors.style && (
                  <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                )}
              </Grid>
            </Grid>
            <Grid>&nbsp;</Grid>
            <Grid container spacing={3} mb={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="pairing-select-label">{M.get('menuItem.pairing')}</InputLabel>
                  <Select
                    labelId="pairing"
                    id="pairing-select"
                    name="pairing"
                    label={`${M.get('menuItem.pairing')}`}
                    value={data.pairing || ''}
                    defaultValue={data.pairing}
                    onChange={wineStore.onChangeField}
                    disabled={disabled}
                    MenuProps={{
                      classes: { paper: classes.menuPaper },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                    }}>
                    <MenuItem key="None" value="">
                      None
                    </MenuItem>
                    {PAIRING.map(item => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="special-select-label">{M.get('menuItem.special')}</InputLabel>
                  <Select
                    labelId="special"
                    id="special-select"
                    name="special"
                    value={data.special || ''}
                    onChange={wineStore.onChangeField}
                    disabled={disabled}
                    defaultValue={data.special}
                    label={M.get('menuItem.special')}
                    MenuProps={{
                      classes: { paper: classes.menuPaper },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'center',
                      },
                    }}>
                    <MenuItem key="None" value="">
                      None
                    </MenuItem>
                    {SPECIALS.map(item => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Divider />
            <Grid container mb={2}>
              <Grid size={{ xs: 12 }}>
                <Typography sx={classes.sectionHeaderText}>
                  {M.get('menuItem.wineSpecs')}{' '}
                  <Tooltip title={M.get('menuItem.wineSpecsTooltip')} placement="top">
                    <HelpOutlineIcon sx={classes.helpIcon} />
                  </Tooltip>
                </Typography>
                <Typography sx={classes.wineSpecsDescription}>{M.get('menuItem.wineSpecsDescription')}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <WineSpecs data={data.menuItemSpecs} label={M.get('menuItem.wineSpecView')} disabled={disabled} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Typography component="h1" sx={classes.sectionHeaderText}>
                  {M.get('menuItem.rating')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Ratings data={data.ratings} disabled={disabled} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Typography component="h1" sx={classes.sectionHeaderText}>
                  {M.get('menuItem.price')}
                </Typography>
                <PriceUnits data={data.menuItemPrices} disabled={disabled} />
              </Grid>
            </Grid>
            <Grid container sx={classes.superVino}>
              <Grid size={{ xs: 12 }}>
                <FormControl>
                  <FormControlLabel
                    sx={classes.checkboxLabel}
                    disabled={disabled}
                    control={
                      <Switch
                        name="isSuperVino"
                        checked={data.isSuperVino}
                        onChange={wineStore.onChangeCheckBox}
                        color="primary"
                      />
                    }
                    labelPlacement="start"
                    label={
                      <Typography sx={classes.fieldHeaderText}>
                        {M.get('menuItem.superVino')}{' '}
                        <Tooltip title={M.get('menuItem.superVinoTooltip')} placement="top">
                          <HelpOutlineIcon sx={classes.helpIcon} />
                        </Tooltip>
                      </Typography>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Divider />
            <Grid container sx={classes.superVino}>
              <Grid size={{ xs: 12 }}>
                <FormControl>
                  <FormControlLabel
                    sx={classes.checkboxLabel}
                    disabled={disabled}
                    control={
                      <Switch
                        name="lookingForImporter"
                        checked={data.lookingForImporter}
                        onChange={wineStore.onChangeCheckBox}
                        color="primary"
                      />
                    }
                    labelPlacement="start"
                    label={
                      <Typography sx={classes.fieldHeaderText}>
                        {M.get('menuItem.lookingForImporter')}{' '}
                        <Tooltip title={M.get('menuItem.lookingForImporterTooltip')} placement="top">
                          <HelpOutlineIcon sx={classes.helpIcon} />
                        </Tooltip>
                      </Typography>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Divider />
            <Grid container sx={classes.superVino}>
              <Grid size={{ xs: 12 }}>
                <FormControl>
                  <FormControlLabel
                    sx={classes.checkboxLabel}
                    disabled={disabled}
                    control={
                      <Switch
                        name="lookingForDistributor"
                        checked={data.lookingForDistributor}
                        onChange={wineStore.onChangeCheckBox}
                        color="primary"
                      />
                    }
                    labelPlacement="start"
                    label={
                      <Typography sx={classes.fieldHeaderText}>
                        {M.get('menuItem.lookingForDistributor')}{' '}
                        <Tooltip title={M.get('menuItem.lookingForDistributorTooltip')} placement="top">
                          <HelpOutlineIcon sx={classes.helpIcon} />
                        </Tooltip>
                      </Typography>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Divider />
            <Grid container>
              <Grid mt={3} size={{ xs: 12 }}>
                <Box
                  mb={2}
                  sx={{
                    ...classes.sectionHeaderText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}>
                  {/* Left: description + tooltip */}
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography sx={classes.fieldHeaderText}>
                        {`${M.get('menuItem.itemImage.description')} *`}
                      </Typography>
                      <Tooltip title={M.get('menuItem.itemImage.tooltip')} placement="top">
                        <HelpOutlineIcon sx={classes.helpIcon} />
                      </Tooltip>
                    </Box>
                    <Typography sx={classes.fieldSubHeaderText}>
                      {M.get('menuItem.itemImage.additionalDescription')}
                    </Typography>
                  </Box>

                  {/* Right: switch + label + tooltip */}
                  {allowedWindowHeight && (
                    <FormControl>
                      <FormControlLabel
                        sx={{ ...classes.checkboxLabel, m: 0 }}
                        disabled={disabled}
                        control={
                          <Switch
                            name="useCrop"
                            checked={useCrop}
                            onChange={e => setUseCrop(e.target.checked)}
                            color="primary"
                          />
                        }
                        labelPlacement="start"
                        label={
                          <Box display="flex" alignItems="center">
                            <Typography sx={classes.fieldHeaderText}>{`${M.get('imageCropper.useCrop')}`}</Typography>
                            <Tooltip title={M.get('imageCropper.useCropTooltip')} placement="top">
                              <HelpOutlineIcon sx={classes.helpIcon} />
                            </Tooltip>
                          </Box>
                        }
                      />
                    </FormControl>
                  )}
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  onClick={() => openModal(WINE_IMAGE_SECTION)}
                  disabled={disabled}
                  sx={{ height: 36, mb: 2 }}>
                  {M.get('menuItem.wineSpecSection.changeTemplate')}
                </Button>

                <FormControl fullWidth variant="filled">
                  {allowedWindowHeight && useCrop ? (
                    <ImageCropWrapper
                      onChange={(origin, medium, small) => {
                        wineStore.onChangeField({
                          target: { value: origin, name: 'image' },
                        });
                        wineStore.onChangeField({
                          target: { value: medium, name: 'imageMedium' },
                        });
                        wineStore.onChangeField({
                          target: { value: small, name: 'imageSmall' },
                        });
                      }}
                      previewClassName="wineCreatePreview"
                      uploadText={M.get('form.dropBottleImageOrUpload')}
                      defaultValue={data.image}
                      disabled={disabled}
                      allowResetToDefault
                      defaultImageValue={wineStore.defaultWineImageUrl}
                      setDisableSave={setDisableSave}
                      errorStyle={!!dataErrors.image}
                      type="wine"
                    />
                  ) : (
                    <ImageInput
                      onChange={e =>
                        wineStore.onChangeField({
                          target: { value: e, name: 'image' },
                        })
                      }
                      previewClassName="wineCreatePreview"
                      uploadText={M.get('form.dropBottleImageOrUpload')}
                      defaultValue={data.image}
                      disabled={disabled}
                      allowResetToDefault
                      defaultImageValue={wineStore.defaultWineImageUrl}
                      setDisableSave={setDisableSave}
                      errorStyle={!!dataErrors.image}
                      type="wine"
                    />
                  )}
                  {dataErrors.image && (
                    <FormHelperText sx={classes.errorHelperText}> {M.get('form.errors.required')} </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </form>
      </Box>
      {/* Footer */}
      <Box sx={classes.pageFooter}>
        <Box>
          {!readonly && (
            <Grid container sx={classes.footerPart}>
              <Button variant="text" color="primary" onClick={disabled || handleDiscard}>
                {M.get('actions.discard')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={disabled || isSaveButtonDisabled()}
                onClick={onSubmit}>
                {isCreate ? M.get('form.create') : M.get('form.save')}
              </Button>
            </Grid>
          )}
        </Box>
      </Box>
      <TemplateImageModal
        open={open}
        onClose={closeModal}
        onSelect={template => {
          const templateUrl = template.url || template;
          wineStore.onChangeField({
            target: { value: templateUrl, name: 'image' },
          });
        }}
        templates={currentTemplates}
        title={M.get('menuItem.templateModal.title').replace('{section}', WINE_IMAGE_SECTION)}
        loading={currentLoading}
        isLandscape={false}
      />
    </Box>
  );
});

CreateMenuItem.propTypes = {
  isEdit: PropTypes.bool,
  isCreate: PropTypes.bool,
};

export default CreateMenuItemWrapper;
